import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringOrNumberAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import { DynamicInferredHeight, DynamicInferredWidth } from '../../../../enums';
import type { TextDynamicBoxBorderRendererSettings } from '../renderer/TextDynamicBoxBorderRenderer';
import { TextDynamicBoxBorderRenderer } from '../renderer/TextDynamicBoxBorderRenderer';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

export class TextAssetDynamicBoxBorder extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_BORDER_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    accessor boxWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    accessor boxHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0 })
    accessor start0 = 0.25;
    @NumberAttribute({ min: 0 })
    accessor end0 = 0.75;
    @NumberAttribute({ min: 0 })
    accessor start1 = 0.75;
    @NumberAttribute({ min: 0 })
    accessor end1 = 0.25;
    @NumberAttribute()
    accessor horizontalPadding = 20;
    @NumberAttribute()
    accessor verticalPadding = 20;
    @NumberAttribute({ min: 0 })
    accessor borderThickness = 5;
    @ColorAttribute()
    accessor borderColor = 0xffffff;
    @AlphaAttribute()
    accessor borderAlpha = 0.5;

    override getCanvasRendererSettings(): TextDynamicBoxBorderRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth =
            typeof this.boxWidth === 'number'
                ? this.boxWidth * this.referenceToOutputMultiplier
                : this.boxWidth;
        const boxHeight =
            typeof this.boxHeight === 'number'
                ? this.boxHeight * this.referenceToOutputMultiplier
                : this.boxHeight;

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            start0: this.start0,
            end0: this.end0,
            start1: this.start1,
            end1: this.end1,
            horizontalPadding: this.horizontalPadding * this.referenceToOutputMultiplier,
            verticalPadding: this.verticalPadding * this.referenceToOutputMultiplier,
            borderThickness: this.borderThickness * this.referenceToOutputMultiplier,
            borderColor: this.borderColor,
            borderAlpha: this.borderAlpha,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since TextDynamicBoxBorder is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return TextDynamicBoxBorderRenderer.animatableProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat(['borderColor', 'borderAlpha']);
    }

    override get animatableGeometricProperties() {
        return TextDynamicBoxBorderRenderer.animatableGeometricProperties;
    }
}
